import React, {useState, useEffect, useCallback} from 'react';
import {View, StyleSheet, ScrollView, Alert} from 'react-native';
import {
  Portal, Dialog, Modal, Text, Button, IconButton, Menu,
  TextInput, ActivityIndicator, Snackbar, Chip,
} from 'react-native-paper';
import {colors, spacing} from '../../theme';
import {workAssignmentApi} from '../../services/api/workAssignmentApi';
import {workerApi} from '../../services/api/workerApi';
import {workerRateApi} from '../../services/api/workerRateApi';
import WorkAssignmentStatusDialog from './WorkAssignmentStatusDialog';

const WorkAssignmentModal = ({visible, onDismiss, orderLineInstanceId, onAssigned, catalogItemId, serviceCatalogItemId}) => {
  const [workers, setWorkers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({visible: false, message: ''});

  // Form state
  const [selectedWorkerId, setSelectedWorkerId] = useState(null);
  const [selectedWorkerName, setSelectedWorkerName] = useState('');
  const [workerMenuVisible, setWorkerMenuVisible] = useState(false);
  const [pieceRate, setPieceRate] = useState('');

  // Rate resolution state
  const [rateSource, setRateSource] = useState(null);
  const [resolvedRateRuleId, setResolvedRateRuleId] = useState(null);
  const [resolvedRate, setResolvedRate] = useState(null);

  // Status dialog
  const [statusDialogVisible, setStatusDialogVisible] = useState(false);
  const [statusAssignment, setStatusAssignment] = useState(null);
  const [menuVisibleId, setMenuVisibleId] = useState(null);

  // Edit rate dialog
  const [editRateDialogVisible, setEditRateDialogVisible] = useState(false);
  const [editRateAssignment, setEditRateAssignment] = useState(null);
  const [editRateValue, setEditRateValue] = useState('');

  const fetchData = useCallback(async () => {
    if (!orderLineInstanceId) return;
    setLoading(true);
    try {
      const [workersRes, assignmentsRes] = await Promise.all([
        workerApi.list(),
        workAssignmentApi.getByInstance(orderLineInstanceId),
      ]);
      setWorkers(workersRes.data?.data?.content || []);
      setAssignments(assignmentsRes.data?.data || []);
    } catch (error) {
      setSnackbar({visible: true, message: error.response?.data?.message || 'Failed to load data'});
    } finally {
      setLoading(false);
    }
  }, [orderLineInstanceId]);

  useEffect(() => {
    if (visible) {
      fetchData();
      setSelectedWorkerId(null);
      setSelectedWorkerName('');
      setPieceRate('');
      setRateSource(null);
      setResolvedRateRuleId(null);
      setResolvedRate(null);
    }
  }, [visible, fetchData]);

  const handleAssign = async () => {
    if (!selectedWorkerId) {
      setSnackbar({visible: true, message: 'Select a worker'});
      return;
    }
    setSubmitting(true);
    try {
      const res = await workAssignmentApi.create({
        orderLineInstanceId,
        workerId: selectedWorkerId,
        pieceRate: pieceRate ? Number(pieceRate) : null,
        resolvedRateRuleId: resolvedRateRuleId || null,
        rateSource: pieceRate ? (rateSource || 'MANUAL_OVERRIDE') : null,
      });
      const assignmentData = res.data?.data;
      if (onAssigned && assignmentData) {
        onAssigned(assignmentData);
      }
      setSelectedWorkerId(null);
      setSelectedWorkerName('');
      setPieceRate('');
      await fetchData();
      onDismiss();
    } catch (error) {
      setSnackbar({visible: true, message: error.response?.data?.message || 'Failed to assign'});
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditRate = async () => {
    if (!editRateAssignment) return;
    try {
      await workAssignmentApi.update(editRateAssignment.id, {
        pieceRate: editRateValue ? Number(editRateValue) : 0,
      });
      setEditRateDialogVisible(false);
      setEditRateAssignment(null);
      fetchData();
    } catch (error) {
      setSnackbar({visible: true, message: error.response?.data?.message || 'Failed to update rate'});
    }
  };

  const handleDelete = (assignment) => {
    Alert.alert(
      'Delete Assignment',
      `Delete assignment for ${assignment.workerName}?`,
      [
        {text: 'Cancel', style: 'cancel'},
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await workAssignmentApi.updateStatus(assignment.id, 'DELETED');
              fetchData();
            } catch (error) {
              setSnackbar({visible: true, message: error.response?.data?.message || 'Failed to delete'});
            }
          },
        },
      ],
    );
  };

  const formatDate = dateStr => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {month: 'short', day: 'numeric'});
  };

  const getStatusColor = status => {
    switch (status) {
      case 'COMPLETED': return colors.success;
      case 'STARTED': return colors.primary;
      case 'DELETED': return colors.error;
      default: return colors.textSecondary;
    }
  };

  const isAssignDisabled = submitting || !selectedWorkerId;

  return (
    <Portal>
      <Modal visible={visible} onDismiss={onDismiss} contentContainerStyle={styles.modalContainer}>
        <View style={styles.titleRow}>
          <Text style={styles.title}>Assign Work</Text>
          <IconButton icon="close" size={20} onPress={onDismiss} />
        </View>
        <ScrollView showsVerticalScrollIndicator={true} style={styles.scrollArea}>
          {loading ? (
            <ActivityIndicator size="small" color={colors.primary} style={{marginVertical: spacing.xl}} />
          ) : (
            <View style={styles.dialogContent}>
              {/* Worker selection */}
              <View style={styles.fieldRow}>
                <Text style={styles.label}>Worker</Text>
                <Menu
                  visible={workerMenuVisible}
                  onDismiss={() => setWorkerMenuVisible(false)}
                  anchor={
                    <Button
                      mode="outlined"
                      onPress={() => setWorkerMenuVisible(true)}
                      style={styles.workerButton}
                      labelStyle={styles.workerButtonLabel}>
                      {selectedWorkerName || 'Select Worker'}
                    </Button>
                  }>
                  {workers.map(w => (
                    <Menu.Item
                      key={w.id}
                      title={w.fullName}
                      onPress={async () => {
                        setSelectedWorkerId(w.id);
                        setSelectedWorkerName(w.fullName);
                        setWorkerMenuVisible(false);
                        // Resolve rate from master
                        if (catalogItemId) {
                          try {
                            const res = await workerRateApi.resolve(w.id, catalogItemId, serviceCatalogItemId);
                            const data = res.data?.data;
                            if (data?.rateValue != null) {
                              setPieceRate(String(data.rateValue));
                              setResolvedRate(String(data.rateValue));
                              setResolvedRateRuleId(data.rateRuleId);
                              setRateSource('MASTER');
                            } else {
                              setPieceRate('');
                              setResolvedRate(null);
                              setResolvedRateRuleId(null);
                              setRateSource(null);
                            }
                          } catch (_e) {
                            // Rate resolution failure is non-critical
                          }
                        }
                      }}
                    />
                  ))}
                </Menu>
              </View>

              {/* Piece rate */}
              <View style={styles.fieldRow}>
                <Text style={styles.label}>Piece Rate</Text>
                <TextInput
                  mode="outlined"
                  value={pieceRate}
                  onChangeText={val => {
                    setPieceRate(val);
                    if (resolvedRate != null && val !== resolvedRate) {
                      setRateSource('MANUAL_OVERRIDE');
                    } else if (resolvedRate != null && val === resolvedRate) {
                      setRateSource('MASTER');
                    }
                  }}
                  keyboardType="numeric"
                  placeholder="0"
                  style={styles.rateInput}
                  dense
                />
              </View>
              {rateSource && (
                <Chip
                  style={[
                    styles.rateSourceChip,
                    {backgroundColor: rateSource === 'MASTER' ? '#E8F5E9' : '#FFF3E0'},
                  ]}
                  textStyle={{
                    fontSize: 11,
                    color: rateSource === 'MASTER' ? colors.success : '#E65100',
                  }}
                  icon={rateSource === 'MASTER' ? 'check-circle' : 'pencil'}
                  compact>
                  {rateSource === 'MASTER' ? 'Master Rate' : 'Manual'}
                </Chip>
              )}

              {/* Assign button */}
              <Button
                mode="contained"
                onPress={handleAssign}
                loading={submitting}
                disabled={isAssignDisabled}
                style={[styles.assignBtn, isAssignDisabled && styles.assignBtnDisabled]}
                labelStyle={isAssignDisabled ? styles.assignBtnLabelDisabled : undefined}
                buttonColor={isAssignDisabled ? colors.border : colors.primary}
                icon="account-hard-hat">
                Assign
              </Button>

              {/* Assignment History */}
              {assignments.length > 0 && (
                <>
                  <View style={styles.divider} />
                  <Text style={styles.subTitle}>Work Assignment History ({assignments.length})</Text>
                  <View style={styles.tableHeader}>
                    <Text style={[styles.headerText, styles.nameCol]}>Worker</Text>
                    <Text style={[styles.headerText, styles.dateCol]}>Assigned</Text>
                    <Text style={[styles.headerText, styles.statusCol]}>Status</Text>
                    <Text style={[styles.headerText, styles.moreCol]}>...</Text>
                  </View>
                  {assignments.map(a => (
                    <View key={a.id} style={styles.row}>
                      <View style={styles.nameCol}>
                        <Text style={styles.cellText}>{a.workerName}</Text>
                        {a.pieceRate != null && (
                          <Text style={styles.priceText}>{'\u20B9'}{Number(a.pieceRate)}</Text>
                        )}
                      </View>
                      <Text style={[styles.cellText, styles.dateCol]}>{formatDate(a.assignedAt)}</Text>
                      <Text style={[styles.cellText, styles.statusCol, {color: getStatusColor(a.status)}]}>
                        {a.status?.replace('_', ' ')}
                      </Text>
                      <View style={styles.moreCol}>
                        <Menu
                          visible={menuVisibleId === a.id}
                          onDismiss={() => setMenuVisibleId(null)}
                          anchor={
                            <IconButton
                              icon="dots-vertical"
                              size={18}
                              onPress={() => setMenuVisibleId(a.id)}
                            />
                          }>
                          <Menu.Item
                            title="Change Status"
                            onPress={() => {
                              setMenuVisibleId(null);
                              setStatusAssignment(a);
                              setStatusDialogVisible(true);
                            }}
                          />
                          <Menu.Item
                            title="Edit Rate"
                            leadingIcon="pencil"
                            onPress={() => {
                              setMenuVisibleId(null);
                              setEditRateAssignment(a);
                              setEditRateValue(a.pieceRate != null ? String(Number(a.pieceRate)) : '');
                              setEditRateDialogVisible(true);
                            }}
                          />
                          <Menu.Item
                            title="Delete"
                            leadingIcon="delete"
                            titleStyle={{color: colors.error}}
                            onPress={() => {
                              setMenuVisibleId(null);
                              handleDelete(a);
                            }}
                          />
                        </Menu>
                      </View>
                    </View>
                  ))}
                </>
              )}
            </View>
          )}
        </ScrollView>
      </Modal>

      {statusAssignment && (
        <WorkAssignmentStatusDialog
          visible={statusDialogVisible}
          onDismiss={() => setStatusDialogVisible(false)}
          assignment={statusAssignment}
          onStatusChanged={fetchData}
        />
      )}

      {/* Edit Rate Dialog */}
      <Dialog visible={editRateDialogVisible} onDismiss={() => setEditRateDialogVisible(false)} style={styles.editRateDialog}>
        <Dialog.Title style={{fontSize: 16}}>Edit Piece Rate</Dialog.Title>
        <Dialog.Content>
          <TextInput
            mode="outlined"
            value={editRateValue}
            onChangeText={setEditRateValue}
            keyboardType="numeric"
            placeholder="0"
            dense
            style={{backgroundColor: colors.surface}}
          />
        </Dialog.Content>
        <Dialog.Actions>
          <Button onPress={() => setEditRateDialogVisible(false)}>Cancel</Button>
          <Button onPress={handleEditRate}>Save</Button>
        </Dialog.Actions>
      </Dialog>

      <Snackbar
        visible={snackbar.visible}
        onDismiss={() => setSnackbar({visible: false, message: ''})}
        duration={3000}>
        {snackbar.message}
      </Snackbar>
    </Portal>
  );
};

const styles = StyleSheet.create({
  modalContainer: {
    backgroundColor: colors.surface,
    marginHorizontal: 26,
    borderRadius: 15,
    maxHeight: '95%',
    overflow: 'hidden',
    justifyContent: 'flex-start',
  },
  dialogContent: {paddingHorizontal: 24, paddingBottom: spacing.md},
  editRateDialog: {borderRadius: 0},
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: spacing.lg,
    paddingRight: spacing.xs,
    paddingVertical: spacing.md,
  },
  title: {fontSize: 18, fontWeight: '700', color: colors.text},
  scrollArea: {},
  fieldRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom:0
  },
  label: {fontSize: 13, fontWeight: '600', color: colors.text},
  workerButton: {borderColor: colors.border, alignSelf: 'flex-start'},
  workerButtonLabel: {fontSize: 13},
  rateInput: {fontSize: 13, height: 38, width: 120},
  rateSourceChip: {alignSelf: 'flex-end', marginBottom: spacing.sm, height: 28},
  assignBtn: {
    borderRadius: 10,
    alignSelf: 'flex-start',
    marginTop: spacing.md,
  },
  assignBtnDisabled: {
    backgroundColor: colors.border,
  },
  assignBtnLabelDisabled: {
    color: colors.textSecondary,
  },
  divider: {
    height: 1,
    backgroundColor: colors.border,
    marginVertical: spacing.md,
  },
  subTitle: {fontSize: 14, fontWeight: '700', color: colors.text, marginBottom: spacing.sm},
  tableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.textSecondary,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.xs,
    borderRadius: 4,
  },
  headerText: {fontSize: 11, fontWeight: '700', color: colors.white},
  nameCol: {flex: 2.5},
  dateCol: {flex: 1.5, textAlign: 'center'},
  statusCol: {flex: 2, textAlign: 'center'},
  moreCol: {width: 36, alignItems: 'center'},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  cellText: {fontSize: 12, color: colors.text},
  priceText: {fontSize: 11, color: colors.textSecondary},
});

export default WorkAssignmentModal;
